import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { hasMinRole, useAuth } from "@/contexts/AuthContext";
import {
  ClipboardText, CheckCircle, XCircle, BookOpen, Bug, Plus,
  Lightbulb, Lock,
} from "@phosphor-icons/react";

const NCR_STAGES = [
  ["open", "Open"], ["in_containment", "Containment"], ["rca", "RCA"],
  ["corrective_action", "CA"], ["verification", "Verify"], ["closed", "Closed"],
  ["reopened", "Reopened"],
];
const NCR_TONE = {
  open: "bg-red-100 text-red-800 border-red-200",
  in_containment: "bg-amber-100 text-amber-800 border-amber-200",
  rca: "bg-orange-100 text-orange-800 border-orange-200",
  corrective_action: "bg-yellow-100 text-yellow-800 border-yellow-200",
  verification: "bg-blue-100 text-blue-800 border-blue-200",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  reopened: "bg-purple-100 text-purple-800 border-purple-200",
};
const SEV_TONE = {
  critical: "bg-red-100 text-red-800",
  major: "bg-orange-100 text-orange-800",
  minor: "bg-slate-100 text-slate-700",
};

export default function ProjectQaCenter({ project, trigger }) {
  const { user } = useAuth();
  const canEdit = hasMinRole(user, "manager");
  const canApprove = hasMinRole(user, "admin");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("reviews");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`qa-${project.id}`}>
            <ClipboardText size={12} className="mr-1" /> QA · NCR · Lessons
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="qa-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ClipboardText size={18} className="text-indigo-600" />
            QA · NCR · Lessons · {project.name}
          </DialogTitle>
          <DialogDescription>
            F-PM-06 Reviews · F-PM-10 Submission Compliance · F-PM-17 Lessons · F-PM-28 NCRs
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {[
            ["reviews", "Internal Reviews", ClipboardText],
            ["submission", "Submission Compliance", CheckCircle],
            ["lessons", "Lessons Learned", Lightbulb],
            ["ncr", "Non-Conformity (NCR)", Bug],
          ].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition ${tab === id ? "border-[#1E5CBA] text-[#1E5CBA]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`qa-tab-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "reviews" && <ReviewsTab project={project} canEdit={canEdit} />}
        {tab === "submission" && <SubmissionTab project={project} canEdit={canEdit} canApprove={canApprove} />}
        {tab === "lessons" && <LessonsTab project={project} canEdit={canEdit} />}
        {tab === "ncr" && <NCRTab project={project} canEdit={canEdit} canApprove={canApprove} />}
      </DialogContent>
    </Dialog>
  );
}

const Loading = () => <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;
const Empty = ({ text }) => <div className="dc-card p-10 text-center text-sm text-slate-500">{text}</div>;

/* ─── INTERNAL REVIEWS (F-PM-06) ─────────────────────────────────────── */
function ReviewsTab({ project, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/internal-reviews?project_id=${project.id}`);
      setRows(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{rows.length} internal reviews</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="review-create-btn">
            <Plus size={11} className="mr-1" /> New review
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No internal reviews yet." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Deliverable</th><th className="px-3 py-2">Author</th><th className="px-3 py-2">Reviewer</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Findings</th><th className="px-3 py-2">Outcome</th><th className="px-3 py-2">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {rows.map((r) => (
                <tr key={r.id} onClick={() => { setEdit(r); setShowForm(true); }} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{r.deliverable_title}<div className="text-[10px] font-mono-alt text-slate-500">{r.deliverable_ref}</div></td>
                  <td className="px-3 py-2 text-xs">{r.author_email}</td>
                  <td className="px-3 py-2 text-xs">{r.reviewer_email}</td>
                  <td className="px-3 py-2 text-xs">{r.review_type}</td>
                  <td className="px-3 py-2 text-xs">{(r.findings || []).length} ({(r.findings || []).filter((f) => f.severity === "critical").length} critical)</td>
                  <td className="px-3 py-2 text-xs">{r.overall_outcome ? r.overall_outcome.replace(/_/g, " ") : "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <ReviewForm projectId={project.id} review={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function ReviewForm({ projectId, review, onClose, onSaved }) {
  const isEdit = !!review?.id;
  const [f, setF] = useState(review || {
    deliverable_ref: "", deliverable_title: "",
    author_email: "", reviewer_email: "",
    review_type: "technical", findings: [],
    overall_outcome: null, author_response: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (signoff = false) => {
    if (!isEdit && f.author_email.trim().toLowerCase() === f.reviewer_email.trim().toLowerCase()) {
      toast.error("Reviewer must differ from author (SOD).");
      return;
    }
    setBusy(true);
    try {
      let id = review?.id;
      if (isEdit) {
        await api.patch(`/internal-reviews/${id}`, f);
      } else {
        const { data } = await api.post("/internal-reviews", { ...f, project_id: projectId });
        id = data.id;
      }
      if (signoff) {
        await api.post(`/internal-reviews/${id}/signoff`);
        toast.success("Review signed off");
      } else {
        toast.success(isEdit ? "Review updated" : "Review created");
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addFinding = () => setF({ ...f, findings: [...(f.findings || []), { severity: "minor", description: "", action: "", status: "open" }] });
  const updateFinding = (i, key, val) => {
    const fnd = [...(f.findings || [])]; fnd[i] = { ...fnd[i], [key]: val }; setF({ ...f, findings: fnd });
  };
  const updateChecklist = (i, key, val) => {
    const cl = [...(f.checklist || [])]; cl[i] = { ...cl[i], [key]: val }; setF({ ...f, checklist: cl });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit review" : "New internal review"}</DialogTitle>
          <DialogDescription>F-PM-06 · Segregation of duties enforced</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><Label>Deliverable reference *</Label><Input value={f.deliverable_ref} onChange={(e) => setF({ ...f, deliverable_ref: e.target.value })} placeholder="e.g. DEL-001" data-testid="review-deliverable-ref" /></div>
          <div><Label>Deliverable title *</Label><Input value={f.deliverable_title} onChange={(e) => setF({ ...f, deliverable_title: e.target.value })} data-testid="review-deliverable-title" /></div>
          <div><Label>Author email *</Label><Input value={f.author_email} onChange={(e) => setF({ ...f, author_email: e.target.value })} data-testid="review-author" /></div>
          <div><Label>Reviewer email * (must differ)</Label><Input value={f.reviewer_email} onChange={(e) => setF({ ...f, reviewer_email: e.target.value })} data-testid="review-reviewer" /></div>
          <div>
            <Label>Review type</Label>
            <Select value={f.review_type} onValueChange={(v) => setF({ ...f, review_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="technical">Technical</SelectItem>
                <SelectItem value="quality">Quality</SelectItem>
                <SelectItem value="peer">Peer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Overall outcome</Label>
            <Select value={f.overall_outcome || ""} onValueChange={(v) => setF({ ...f, overall_outcome: v })}>
              <SelectTrigger data-testid="review-outcome"><SelectValue placeholder="Set before sign-off" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="passed_with_minor">Passed with minor</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isEdit && f.checklist?.length > 0 && (
            <div className="col-span-2">
              <div className="overline mb-1">Checklist ({f.checklist.filter((c) => c.met === true).length}/{f.checklist.length} met)</div>
              <ul className="space-y-1 max-h-44 overflow-y-auto rounded-lg border border-slate-200 p-2 text-xs">
                {f.checklist.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 py-1 border-b border-slate-100 last:border-0">
                    <Select value={c.met == null ? "pending" : (c.met ? "met" : "notmet")} onValueChange={(v) => updateChecklist(i, "met", v === "met" ? true : v === "notmet" ? false : null)}>
                      <SelectTrigger className="w-24 h-7 text-[10px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="met">Met</SelectItem>
                        <SelectItem value="notmet">Not met</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="flex-1 text-slate-700">{c.criterion}</span>
                    <Input className="h-7 w-40 text-xs" placeholder="Comments" value={c.comments || ""} onChange={(e) => updateChecklist(i, "comments", e.target.value)} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1">
              <div className="overline">Findings</div>
              <Button size="sm" variant="outline" onClick={addFinding} data-testid="review-add-finding"><Plus size={10} className="mr-1" /> Add</Button>
            </div>
            {(f.findings || []).map((fnd, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 mb-1">
                <Select value={fnd.severity} onValueChange={(v) => updateFinding(i, "severity", v)}>
                  <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                  </SelectContent>
                </Select>
                <Input className="col-span-5 h-8 text-xs" placeholder="Description" value={fnd.description} onChange={(e) => updateFinding(i, "description", e.target.value)} />
                <Input className="col-span-5 h-8 text-xs" placeholder="Action" value={fnd.action || ""} onChange={(e) => updateFinding(i, "action", e.target.value)} />
              </div>
            ))}
          </div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} variant="outline" onClick={() => submit(false)} data-testid="review-save">
            {isEdit ? "Save changes" : "Save draft"}
          </Button>
          {isEdit && f.status !== "submitted" && f.status !== "closed" && (
            <Button disabled={busy || !f.overall_outcome} className="btn-primary-blue" onClick={() => submit(true)} data-testid="review-signoff">
              <Lock size={11} className="mr-1" /> Sign off
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── SUBMISSION COMPLIANCE (F-PM-10) ───────────────────────────────── */
function SubmissionTab({ project, canEdit, canApprove }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/submission-compliance?project_id=${project.id}`);
      setRows(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{rows.length} submission checklists</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="sub-create-btn">
            <Plus size={11} className="mr-1" /> New checklist
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No submission checklists yet." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Submission</th><th className="px-3 py-2">Client</th><th className="px-3 py-2">Items</th><th className="px-3 py-2">Completion</th><th className="px-3 py-2">Overall</th><th className="px-3 py-2">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {rows.map((s) => (
                <tr key={s.id} onClick={() => { setEdit(s); setShowForm(true); }} className="cursor-pointer hover:bg-slate-50" data-testid={`sub-row-${s.id}`}>
                  <td className="px-3 py-2 font-medium">{s.submission_title}</td>
                  <td className="px-3 py-2 text-xs">{s.client_name || "—"}</td>
                  <td className="px-3 py-2 font-mono-alt text-xs">{s.items_met}/{s.items_total}</td>
                  <td className="px-3 py-2 font-mono-alt text-xs">{s.completion_pct}%</td>
                  <td className="px-3 py-2">{s.overall_passed ? <span className="inline-block rounded px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-800 font-mono-alt">PASS</span> : <span className="inline-block rounded px-2 py-0.5 text-[10px] bg-amber-100 text-amber-800 font-mono-alt">PENDING</span>}</td>
                  <td className="px-3 py-2 text-xs">{s.status?.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <SubmissionForm projectId={project.id} sub={edit} canApprove={canApprove} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function SubmissionForm({ projectId, sub, canApprove, onClose, onSaved }) {
  const isEdit = !!sub?.id;
  const [f, setF] = useState(sub || {
    submission_title: "", client_name: "", submission_date: "",
    notes: "", checklist: [],
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      let id = sub?.id;
      if (isEdit) await api.patch(`/submission-compliance/${id}`, f);
      else {
        const { data } = await api.post("/submission-compliance", { ...f, project_id: projectId });
        id = data.id;
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/submission-compliance/${sub.id}/approve`);
      toast.success("Approved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markSubmitted = async () => {
    setBusy(true);
    try {
      await api.post(`/submission-compliance/${sub.id}/mark-submitted?client_acknowledged=true`);
      toast.success("Marked submitted to client");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateCheck = (i, key, val) => {
    const cl = [...(f.checklist || [])]; cl[i] = { ...cl[i], [key]: val }; setF({ ...f, checklist: cl });
  };
  const metCount = (f.checklist || []).filter((c) => c.met === true).length;
  const failedCount = (f.checklist || []).filter((c) => c.met === false).length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit submission checklist" : "New submission checklist"}</DialogTitle>
          <DialogDescription>F-PM-10 · Pre-client gate</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label>Submission title *</Label><Input value={f.submission_title} onChange={(e) => setF({ ...f, submission_title: e.target.value })} data-testid="sub-title" /></div>
          <div><Label>Client</Label><Input value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} /></div>
          <div><Label>Submission date</Label><Input type="date" value={f.submission_date || ""} onChange={(e) => setF({ ...f, submission_date: e.target.value })} /></div>
          <div className="col-span-2">
            {isEdit ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Checklist · {metCount}/{(f.checklist || []).length} met · {failedCount} failed</div>
                  <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${f.overall_passed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {f.overall_passed ? "PASS" : "PENDING"}
                  </span>
                </div>
                <ul className="space-y-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 p-2">
                  {(f.checklist || []).map((c, i) => (
                    <li key={i} className="flex items-start gap-2 py-1 border-b border-slate-100 last:border-0 text-xs">
                      <Select value={c.met == null ? "pending" : (c.met ? "met" : "notmet")} onValueChange={(v) => updateCheck(i, "met", v === "met" ? true : v === "notmet" ? false : null)}>
                        <SelectTrigger className="w-24 h-7 text-[10px]" data-testid={`sub-check-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="met">Met</SelectItem>
                          <SelectItem value="notmet">Not met</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="flex-1 text-slate-700">{c.criterion}</span>
                      <Input className="h-7 w-44 text-xs" placeholder="Notes" value={c.notes || ""} onChange={(e) => updateCheck(i, "notes", e.target.value)} />
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs text-blue-800">
                Default 9-item checklist will be auto-attached. Tick items after saving.
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} variant="outline" onClick={submit} data-testid="sub-save">{isEdit ? "Save changes" : "Create"}</Button>
          {isEdit && f.status === "draft" && canApprove && (
            <Button disabled={busy || !f.overall_passed} className="btn-primary-blue" onClick={approve} data-testid="sub-approve">
              <CheckCircle size={11} className="mr-1" /> PD approve
            </Button>
          )}
          {isEdit && f.status === "passed" && (
            <Button disabled={busy} className="btn-primary-blue" onClick={markSubmitted} data-testid="sub-mark-submitted">
              Mark submitted to client
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── LESSONS LEARNED (F-PM-17) ──────────────────────────────────────── */
function LessonsTab({ project, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [scope, setScope] = useState("project"); // project | global

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (scope === "project") params.append("project_id", project.id);
      if (q) params.append("q", q);
      if (cat !== "all") params.append("category", cat);
      const { data } = await api.get(`/lessons-learned?${params}`);
      setRows(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, cat]);

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {[["project", "This project"], ["global", "All projects"]].map(([id, label]) => (
            <button key={id} onClick={() => setScope(id)} className={`px-3 py-1 text-xs font-medium rounded ${scope === id ? "bg-white shadow text-[#1E5CBA]" : "text-slate-500"}`} data-testid={`lesson-scope-${id}`}>
              {label}
            </button>
          ))}
        </div>
        <Input className="flex-1 min-w-[160px] h-8" placeholder="Search lessons…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="lesson-search" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {["technical", "commercial", "schedule", "stakeholder", "qa", "safety", "team", "process", "risk", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load}>Search</Button>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue ml-auto" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="lesson-create-btn">
            <Plus size={11} className="mr-1" /> Log lesson
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No lessons match the current filters." /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((l) => (
            <div key={l.id} onClick={() => { setEdit(l); setShowForm(true); }} className="cursor-pointer rounded-xl border border-slate-200 bg-white p-3 hover:border-[#1E5CBA] transition" data-testid={`lesson-card-${l.id}`}>
              <div className="flex items-center justify-between mb-1">
                <h5 className="font-display text-sm font-semibold text-slate-900 line-clamp-2">{l.lesson_title}</h5>
                <span className={`inline-block shrink-0 rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${l.priority === "high" ? "bg-red-100 text-red-700" : l.priority === "medium" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>{l.priority}</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono-alt text-slate-500 mb-2">
                <BookOpen size={10} /> {l.category} · {l.applicability?.replace(/_/g, " ")} · {l.status}
              </div>
              {scope === "global" && <div className="text-[10px] text-slate-400 mb-1">{l.project_name}</div>}
              <p className="text-xs text-slate-600 line-clamp-2">{l.recommendation}</p>
              {l.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {l.tags.map((t, i) => <span key={i} className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-mono-alt">{t}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showForm && <LessonForm projectId={project.id} lesson={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function LessonForm({ projectId, lesson, onClose, onSaved }) {
  const isEdit = !!lesson?.id;
  const [f, setF] = useState(lesson || {
    lesson_title: "", category: "process", what_happened: "",
    root_cause: "", impact: "", recommendation: "",
    applicability: "all_similar", priority: "medium", tags: [],
    status: "logged",
  });
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/lessons-learned/${lesson.id}`, f);
      else await api.post("/lessons-learned", { ...f, project_id: projectId });
      toast.success(isEdit ? "Lesson updated" : "Lesson logged");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit lesson" : "Log lesson learned"}</DialogTitle>
          <DialogDescription>F-PM-17 · Cross-project searchable register</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label>Lesson title *</Label><Input value={f.lesson_title} onChange={(e) => setF({ ...f, lesson_title: e.target.value })} data-testid="lesson-title" /></div>
          <div>
            <Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["technical", "commercial", "schedule", "stakeholder", "qa", "safety", "team", "process", "risk", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Applicability</Label>
            <Select value={f.applicability} onValueChange={(v) => setF({ ...f, applicability: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this_project">This project</SelectItem>
                <SelectItem value="all_similar">All similar projects</SelectItem>
                <SelectItem value="all_projects">All projects</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isEdit && (
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="logged">Logged</SelectItem>
                  <SelectItem value="under_review">Under review</SelectItem>
                  <SelectItem value="adopted">Adopted</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2"><Label>What happened *</Label><Textarea rows={2} value={f.what_happened} onChange={(e) => setF({ ...f, what_happened: e.target.value })} /></div>
          <div className="col-span-2"><Label>Root cause</Label><Textarea rows={2} value={f.root_cause || ""} onChange={(e) => setF({ ...f, root_cause: e.target.value })} /></div>
          <div className="col-span-2"><Label>Recommendation *</Label><Textarea rows={2} value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} data-testid="lesson-rec" /></div>
          <div className="col-span-2">
            <Label>Tags</Label>
            <div className="flex gap-1">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag and press Enter" onKeyDown={(e) => {
                if (e.key === "Enter" && tagInput.trim()) {
                  e.preventDefault();
                  setF({ ...f, tags: [...(f.tags || []), tagInput.trim()] });
                  setTagInput("");
                }
              }} />
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {(f.tags || []).map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-mono-alt">
                  {t}
                  <button onClick={() => setF({ ...f, tags: f.tags.filter((_, j) => j !== i) })} className="hover:text-red-600">×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.lesson_title || !f.recommendation} className="btn-primary-blue" onClick={submit} data-testid="lesson-save">
            {busy ? "Saving…" : isEdit ? "Update" : "Log lesson"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── NCR (F-PM-28) ──────────────────────────────────────────────────── */
function NCRTab({ project, canEdit, canApprove }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ncrs?project_id=${project.id}`);
      setRows(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{rows.length} non-conformity reports</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => setShowForm(true)} data-testid="ncr-create-btn">
            <Plus size={11} className="mr-1" /> New NCR
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No NCRs raised on this project. " /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">NCR #</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Severity</th><th className="px-3 py-2">Stage</th><th className="px-3 py-2">Raised</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {rows.map((n) => (
                <tr key={n.id} onClick={() => setSelected(n)} className="cursor-pointer hover:bg-slate-50" data-testid={`ncr-row-${n.id}`}>
                  <td className="px-3 py-2 font-mono-alt text-xs font-semibold">{n.ncr_number}</td>
                  <td className="px-3 py-2 line-clamp-2">{n.description}</td>
                  <td className="px-3 py-2 text-xs">{n.nc_type}</td>
                  <td className="px-3 py-2"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${SEV_TONE[n.severity]}`}>{n.severity}</span></td>
                  <td className="px-3 py-2"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${NCR_TONE[n.status]}`}>{n.status?.replace(/_/g, " ")}</span></td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">{new Date(n.raised_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <NCRCreateForm projectId={project.id} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {selected && <NCRStageDrawer ncr={selected} canApprove={canApprove} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function NCRCreateForm({ projectId, onClose, onSaved }) {
  const [f, setF] = useState({ description: "", nc_type: "product", severity: "minor", deliverable_ref: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/ncrs", { ...f, project_id: projectId });
      toast.success("NCR raised");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Raise NCR</DialogTitle>
          <DialogDescription>F-PM-28 · Number auto-assigned</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label>Description *</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ncr-desc" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Type</Label>
              <Select value={f.nc_type} onValueChange={(v) => setF({ ...f, nc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="product">Product</SelectItem><SelectItem value="process">Process</SelectItem><SelectItem value="system">System</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={f.severity} onValueChange={(v) => setF({ ...f, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="major">Major</SelectItem><SelectItem value="minor">Minor</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Deliverable ref</Label><Input value={f.deliverable_ref} onChange={(e) => setF({ ...f, deliverable_ref: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.description} className="btn-primary-blue" onClick={submit} data-testid="ncr-save">{busy ? "Saving…" : "Raise NCR"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NCRStageDrawer({ ncr, canApprove, onClose, onSaved }) {
  const [stage, setStage] = useState({});
  const [busy, setBusy] = useState(false);

  const post = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/ncrs/${ncr.id}/${path}`, body);
      toast.success("Stage advanced");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const stageIndex = NCR_STAGES.findIndex((s) => s[0] === ncr.status);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Bug size={16} /> {ncr.ncr_number}
          </DialogTitle>
          <DialogDescription>
            <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase mr-2 ${SEV_TONE[ncr.severity]}`}>{ncr.severity}</span>
            <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${NCR_TONE[ncr.status]}`}>{ncr.status?.replace(/_/g, " ")}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Stage tracker */}
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {NCR_STAGES.slice(0, 6).map((s, i) => (
            <React.Fragment key={s[0]}>
              <div className={`rounded-lg border px-3 py-2 text-xs ${i <= stageIndex && ncr.status !== "reopened" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-500"} ${i === stageIndex ? "ring-2 ring-blue-300" : ""}`}>
                <div className="font-display text-sm font-semibold">{i + 1}</div>
                <div className="text-[10px] uppercase tracking-wider">{s[1]}</div>
              </div>
              {i < 5 && <span className="text-slate-400">→</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
          <div className="overline mb-1">Description</div>
          <p className="text-slate-700">{ncr.description}</p>
          <div className="mt-2 text-[10px] text-slate-500">Raised by {ncr.raised_by} · {new Date(ncr.raised_at).toLocaleString()}</div>
        </div>

        {/* Stage forms — only show the next-available */}
        {ncr.status === "open" && (
          <StageForm title="Containment action" hint="Immediately contain the non-conformity to prevent escape."
            fields={[
              ["action", "Containment action *", "textarea"],
              ["notes", "Notes", "textarea"],
            ]}
            initial={{ effective: true }}
            onSubmit={(v) => post("containment", v)} busy={busy} testId="ncr-containment" />
        )}
        {ncr.status === "in_containment" && (
          <StageForm title="Root cause analysis"
            fields={[
              ["method", "Method *", "select", ["5_whys", "fishbone", "pareto", "fault_tree", "other"]],
              ["analysis", "Analysis *", "textarea"],
              ["identified_cause", "Identified root cause *", "textarea"],
            ]}
            initial={{ method: "5_whys" }}
            onSubmit={(v) => post("rca", v)} busy={busy} testId="ncr-rca" />
        )}
        {(ncr.status === "rca" || ncr.status === "reopened") && (
          <StageForm title="Corrective action plan"
            fields={[
              ["actions_csv", "Actions (one per line) *", "textarea"],
              ["owner_email", "Owner email *", "input"],
              ["target_date", "Target date *", "date"],
              ["notes", "Notes", "textarea"],
            ]}
            transform={(v) => ({ ...v, actions: (v.actions_csv || "").split("\n").map((x) => x.trim()).filter(Boolean), actions_csv: undefined })}
            onSubmit={(v) => post("corrective-action", v)} busy={busy} testId="ncr-ca" />
        )}
        {ncr.status === "corrective_action" && (
          <StageForm title="Verification of effectiveness"
            fields={[
              ["method", "Verification method *", "input"],
              ["evidence_url", "Evidence URL / link", "input"],
              ["effective", "Effective?", "select", [["true", "Yes — effective"], ["false", "No — return to RCA"]]],
              ["notes", "Notes", "textarea"],
            ]}
            initial={{ effective: "true" }}
            transform={(v) => ({ ...v, effective: v.effective === "true" || v.effective === true })}
            onSubmit={(v) => post("verify", v)} busy={busy} testId="ncr-verify" />
        )}
        {ncr.status === "verification" && canApprove && (
          <StageForm title="Close NCR" hint="Final closure once verification confirmed effective."
            fields={[["comments", "Closure comments", "textarea"]]}
            onSubmit={(v) => post("close", v)} busy={busy} testId="ncr-close" />
        )}
        {ncr.status === "closed" && canApprove && (
          <StageForm title="Reopen NCR" hint="Only do this if recurrence is detected."
            fields={[["comments", "Reason for reopening *", "textarea"]]}
            onSubmit={(v) => post("reopen", v)} busy={busy} testId="ncr-reopen" />
        )}

        {/* Stage history */}
        {ncr.history?.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-3 py-2 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">History</div>
            <ul className="divide-y divide-slate-100 max-h-44 overflow-y-auto text-xs">
              {ncr.history.map((h, i) => (
                <li key={i} className="px-3 py-1.5">
                  <strong>{h.action?.replace(/_/g, " ")}</strong>
                  {h.new_status && <span className="ml-1 text-slate-500">→ {h.new_status}</span>}
                  <div className="text-[10px] text-slate-500">{h.by} · {new Date(h.at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageForm({ title, hint, fields, initial = {}, onSubmit, transform, busy, testId }) {
  const [v, setV] = useState(initial);
  const required = fields.filter(([_, label]) => label.includes("*")).map(([k]) => k);
  const allOk = required.every((k) => (v[k] || "").toString().trim().length > 0);
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-2">
      <div className="text-sm font-semibold text-blue-900">{title}</div>
      {hint && <div className="text-[11px] text-blue-700">{hint}</div>}
      <div className="grid grid-cols-2 gap-2 text-sm">
        {fields.map(([k, label, type, opts]) => {
          const col = type === "textarea" ? "col-span-2" : "";
          return (
            <div key={k} className={col}>
              <Label className="text-[11px]">{label}</Label>
              {type === "select" ? (
                <Select value={v[k] || ""} onValueChange={(val) => setV({ ...v, [k]: val })}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(opts || []).map((o) => {
                      if (Array.isArray(o)) return <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>;
                      return <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              ) : type === "textarea" ? (
                <Textarea rows={2} value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
              ) : (
                <Input type={type || "text"} value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
              )}
            </div>
          );
        })}
      </div>
      <Button disabled={busy || !allOk} className="btn-primary-blue w-full" onClick={() => onSubmit(transform ? transform(v) : v)} data-testid={testId}>
        {busy ? "Saving…" : title.startsWith("Close") ? "Close NCR" : "Advance stage"}
      </Button>
    </div>
  );
}
